import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Plus, Trash2, Layers, GitBranch, Calculator, TrendingUp, AlertTriangle, Target, Clock, Calendar, SlidersHorizontal } from 'lucide-react';
import { getPeriodicityLabel } from '../../lib/utils';

const KPIForm = ({
  open,
  onOpenChange,
  editingKPI,
  users,
  onSubmit,
  saving,
  isAdmin = false,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    area: '',
    responsible_id: '',
    unit: 'unidades',
    frequency: 'PER',
    evaluation_type: 'percentage',
    goal_calculation_type: 'accumulated',
    periodicity: 'weekly',
    periodicity_start_month: null, // Only for quarterly/semiannual
    is_active: true,
    has_hierarchy: false,
    hierarchy_dimension: 'Estrategia',
    hierarchy_nodes: [],
  });
  
  const [newNodeName, setNewNodeName] = useState('');
  const [newChildName, setNewChildName] = useState('');
  const [selectedParentForChild, setSelectedParentForChild] = useState(null);
  const [showPeriodicityWarning, setShowPeriodicityWarning] = useState(false);
  const [pendingPeriodicity, setPendingPeriodicity] = useState(null);

  // Reset form when dialog opens/closes or editingKPI changes
  React.useEffect(() => {
    if (open) {
      if (editingKPI) {
        setFormData({
          name: editingKPI.name,
          area: editingKPI.area,
          responsible_id: editingKPI.responsible_id,
          unit: editingKPI.unit,
          frequency: editingKPI.frequency,
          evaluation_type: editingKPI.evaluation_type,
          goal_calculation_type: editingKPI.goal_calculation_type || 'accumulated',
          periodicity: editingKPI.periodicity || 'weekly',
          periodicity_start_month: editingKPI.periodicity_start_month || null,
          goal_periodicity: editingKPI.goal_periodicity || 'bimonthly',
          is_active: editingKPI.is_active,
          has_hierarchy: editingKPI.has_hierarchy || false,
          hierarchy_dimension: editingKPI.hierarchy_dimension || 'Estrategia',
          hierarchy_nodes: editingKPI.hierarchy_nodes || [],
        });
      } else {
        setFormData({
          name: '',
          area: '',
          responsible_id: '',
          unit: 'unidades',
          frequency: 'PER',
          evaluation_type: 'percentage',
          goal_calculation_type: 'accumulated',
          periodicity: 'weekly',
          periodicity_start_month: null,
          goal_periodicity: 'bimonthly',
          is_active: true,
          has_hierarchy: false,
          hierarchy_dimension: 'Estrategia',
          hierarchy_nodes: [],
        });
      }
      setNewNodeName('');
      setNewChildName('');
      setSelectedParentForChild(null);
      setShowPeriodicityWarning(false);
      setPendingPeriodicity(null);
    }
  }, [open, editingKPI]);

  // Handle periodicity change with warning for existing KPIs
  const handlePeriodicityChange = (newPeriodicity) => {
    if (editingKPI && editingKPI.has_results) {
      // KPI has history, show warning
      setPendingPeriodicity(newPeriodicity);
      setShowPeriodicityWarning(true);
    } else {
      setFormData({ ...formData, periodicity: newPeriodicity });
    }
  };

  const confirmPeriodicityChange = () => {
    if (pendingPeriodicity) {
      setFormData({ ...formData, periodicity: pendingPeriodicity });
    }
    setShowPeriodicityWarning(false);
    setPendingPeriodicity(null);
  };

  const cancelPeriodicityChange = () => {
    setShowPeriodicityWarning(false);
    setPendingPeriodicity(null);
  };

  const addParentNode = () => {
    if (!newNodeName.trim()) return;
    const newNode = {
      id: `node_${Date.now()}`,
      name: newNodeName.trim(),
      parent_id: null,
      children: [],
      is_leaf: true,
    };
    setFormData({
      ...formData,
      hierarchy_nodes: [...formData.hierarchy_nodes, newNode],
    });
    setNewNodeName('');
  };

  const addChildNode = (parentId) => {
    if (!newChildName.trim()) return;
    const newNode = {
      id: `node_${Date.now()}`,
      name: newChildName.trim(),
      parent_id: parentId,
      children: [],
      is_leaf: true,
    };
    
    const updatedNodes = formData.hierarchy_nodes.map(node => {
      if (node.id === parentId) {
        return { ...node, is_leaf: false, children: [...(node.children || []), newNode.id] };
      }
      return node;
    });
    
    setFormData({
      ...formData,
      hierarchy_nodes: [...updatedNodes, newNode],
    });
    setNewChildName('');
    setSelectedParentForChild(null);
  };

  const removeNode = (nodeId) => {
    const nodeToRemove = formData.hierarchy_nodes.find(n => n.id === nodeId);
    if (!nodeToRemove) return;
    
    const getDescendants = (id) => {
      const children = formData.hierarchy_nodes.filter(n => n.parent_id === id);
      return [id, ...children.flatMap(c => getDescendants(c.id))];
    };
    const idsToRemove = getDescendants(nodeId);
    
    let updatedNodes = formData.hierarchy_nodes.filter(n => !idsToRemove.includes(n.id));
    
    if (nodeToRemove.parent_id) {
      const parentId = nodeToRemove.parent_id;
      const remainingSiblings = updatedNodes.filter(n => n.parent_id === parentId);
      if (remainingSiblings.length === 0) {
        updatedNodes = updatedNodes.map(n => {
          if (n.id === parentId) {
            return { ...n, is_leaf: true, children: [] };
          }
          return n;
        });
      } else {
        updatedNodes = updatedNodes.map(n => {
          if (n.id === parentId) {
            return { ...n, children: remainingSiblings.map(s => s.id) };
          }
          return n;
        });
      }
    }
    
    setFormData({ ...formData, hierarchy_nodes: updatedNodes });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSubmit(formData, editingKPI);
  };

  const handleClose = (open) => {
    if (!open) {
      setNewNodeName('');
      setNewChildName('');
      setSelectedParentForChild(null);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col" data-testid="kpi-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Manrope']">{editingKPI ? 'Editar KPI' : 'Nuevo KPI'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 overflow-y-auto flex-1 pr-2">
          <div className="space-y-2">
            <Label htmlFor="name">Nombre del KPI</Label>
            <Input 
              id="name" 
              value={formData.name} 
              onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
              required 
              data-testid="kpi-name-input" 
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="area">Área</Label>
            <Input 
              id="area" 
              value={formData.area} 
              onChange={(e) => setFormData({ ...formData, area: e.target.value })} 
              required 
              data-testid="kpi-area-input" 
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="responsible">Responsable</Label>
            <Select value={formData.responsible_id} onValueChange={(v) => setFormData({ ...formData, responsible_id: v })}>
              <SelectTrigger data-testid="kpi-responsible-select">
                <SelectValue placeholder="Selecciona" />
              </SelectTrigger>
              <SelectContent>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Unidad</Label>
              <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unidades">Unidades</SelectItem>
                  <SelectItem value="pares">Pares</SelectItem>
                  <SelectItem value="pesos">Pesos</SelectItem>
                  <SelectItem value="piezas">Piezas</SelectItem>
                  <SelectItem value="porcentaje">Porcentaje</SelectItem>
                  <SelectItem value="horas">Horas</SelectItem>
                  <SelectItem value="dias">Días</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Evaluación</Label>
              <Select 
                value={formData.evaluation_type} 
                onValueChange={(v) => setFormData({ ...formData, evaluation_type: v })} 
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Mayor es mejor</SelectItem>
                  <SelectItem value="threshold">Menor es mejor</SelectItem>
                </SelectContent>
              </Select>
              {editingKPI && (
                <p className="text-xs text-slate-500">
                  Cambiar el criterio de evaluación actualizará la visualización del KPI sin afectar los datos históricos.
                </p>
              )}
            </div>
          </div>

          {/* Goal Calculation Type */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Calculator className="w-4 h-4 text-slate-600" />
              <Label>Tipo de cálculo de meta</Label>
            </div>
            <Select 
              value={formData.goal_calculation_type} 
              onValueChange={(v) => setFormData({ ...formData, goal_calculation_type: v })}
            >
              <SelectTrigger data-testid="goal-calculation-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="accumulated">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-500" />
                    <div>
                      <span className="font-medium">Acumulado</span>
                      <p className="text-xs text-slate-500">Suma de periodos (ventas, producción)</p>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="average">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-4 h-4 text-violet-500" />
                    <div>
                      <span className="font-medium">Promedio (Meta fija)</span>
                      <p className="text-xs text-slate-500">Meta fija por periodo, resultado promediado (tasas, %)</p>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="fixed_per_period">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-amber-500" />
                    <div>
                      <span className="font-medium">Meta fija por periodo (límite)</span>
                      <p className="text-xs text-slate-500">Cada periodo se evalúa contra el mismo límite fijo</p>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="range">
                  <div className="flex items-center gap-2">
                    <SlidersHorizontal className="w-4 h-4 text-emerald-500" />
                    <div>
                      <span className="font-medium">Meta por rango</span>
                      <p className="text-xs text-slate-500">En meta si el resultado cae entre un mínimo y un máximo</p>
                    </div>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400">
              {formData.goal_calculation_type === 'average' 
                ? 'Meta fija: cada periodo tendrá la misma meta. El resultado se calculará como promedio.'
                : formData.goal_calculation_type === 'fixed_per_period'
                ? 'Meta fija (límite): cada periodo se evalúa independientemente contra la misma meta. El resumen muestra el promedio.'
                : formData.goal_calculation_type === 'range'
                ? 'Meta por rango: define un mínimo y un máximo. El resultado es verde si cae dentro del rango (incluyendo los extremos) y rojo si queda fuera. Sin porcentaje ni zona amarilla.'
                : 'Acumulado: la meta se distribuye entre periodos. El resultado se suma.'}
            </p>
            {editingKPI && editingKPI.goal_calculation_type !== formData.goal_calculation_type && (
              <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  <strong>Advertencia:</strong> Cambiar el tipo de cálculo afectará cómo se interpretan las metas existentes. 
                  Las metas por periodo se recalcularán según el nuevo tipo cuando se guarde o edite una meta.
                </p>
              </div>
            )}
          </div>

          {/* Periodicity Configuration */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-600" />
              <Label>Periodicidad de evaluación</Label>
            </div>
            <Select 
              value={formData.periodicity} 
              onValueChange={handlePeriodicityChange}
              disabled={editingKPI && !isAdmin}
            >
              <SelectTrigger data-testid="periodicity-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-500" />
                    <div>
                      <span className="font-medium">Semanal</span>
                      <p className="text-xs text-slate-500">Evaluación cada semana (jueves a miércoles)</p>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="monthly">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-emerald-500" />
                    <div>
                      <span className="font-medium">Mensual</span>
                      <p className="text-xs text-slate-500">Evaluación cada mes calendario</p>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="bimonthly">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-violet-500" />
                    <div>
                      <span className="font-medium">Bimestral</span>
                      <p className="text-xs text-slate-500">Evaluación cada dos meses (Ene-Feb, Mar-Abr, etc.)</p>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="quarterly">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-cyan-500" />
                    <div>
                      <span className="font-medium">Trimestral</span>
                      <p className="text-xs text-slate-500">Evaluación cada trimestre (4 periodos por año)</p>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="semiannual">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-amber-500" />
                    <div>
                      <span className="font-medium">Semestral</span>
                      <p className="text-xs text-slate-500">Evaluación cada semestre (2 periodos por año)</p>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="annual">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-rose-500" />
                    <div>
                      <span className="font-medium">Anual</span>
                      <p className="text-xs text-slate-500">Evaluación anual (1 periodo por año)</p>
                    </div>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400">
              {formData.periodicity === 'weekly' 
                ? 'Se evalúa cada semana (52 periodos por año)'
                : formData.periodicity === 'monthly'
                ? 'Se evalúa cada mes calendario (12 periodos por año)'
                : formData.periodicity === 'bimonthly'
                ? 'Se evalúa cada bimestre (6 periodos por año)'
                : formData.periodicity === 'quarterly'
                ? 'Se evalúa cada trimestre (4 periodos por año)'
                : formData.periodicity === 'annual'
                ? 'Se evalúa una vez al año (1 periodo por año)'
                : 'Se evalúa cada semestre (2 periodos por año)'}
            </p>
            {editingKPI && !isAdmin && (
              <p className="text-xs text-amber-600">
                Solo administradores pueden cambiar la periodicidad de un KPI existente.
              </p>
            )}
          </div>

          {/* Goal Periodicity - Only for weekly KPIs */}
          {formData.periodicity === 'weekly' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-600" />
                <Label>Agrupación de metas</Label>
              </div>
              <Select 
                value={formData.goal_periodicity || 'bimonthly'} 
                onValueChange={(v) => setFormData({ ...formData, goal_periodicity: v })}
              >
                <SelectTrigger data-testid="goal-periodicity-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">
                    <span className="font-medium">Mensual</span>
                    <span className="text-xs text-slate-500 ml-2">12 metas por año</span>
                  </SelectItem>
                  <SelectItem value="bimonthly">
                    <span className="font-medium">Bimestral</span>
                    <span className="text-xs text-slate-500 ml-2">6 metas por año</span>
                  </SelectItem>
                  <SelectItem value="quarterly">
                    <span className="font-medium">Trimestral</span>
                    <span className="text-xs text-slate-500 ml-2">4 metas por año</span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">
                {formData.goal_periodicity === 'quarterly'
                  ? 'La meta se define por trimestre y se distribuye entre las semanas del trimestre. La captura sigue siendo semanal.'
                  : formData.goal_periodicity === 'monthly'
                  ? 'La meta se define por mes y se distribuye entre las semanas del mes (12 metas al año). La captura sigue siendo semanal.'
                  : 'La meta se define por bimestre y se distribuye entre las semanas del bimestre. La captura sigue siendo semanal.'}
              </p>
              {editingKPI && formData.goal_periodicity !== editingKPI.goal_periodicity && (
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs" data-testid="goal-periodicity-change-warning">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Este cambio aplicará solo a partir de hoy. Las metas y periodos ya definidos no se modifican.</span>
                </div>
              )}
            </div>
          )}

          {/* Start Month Configuration - Only for Quarterly and Semiannual */}
          {(formData.periodicity === 'quarterly' || formData.periodicity === 'semiannual') && (
            <div className="space-y-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-600" />
                <Label>Mes de inicio del ciclo</Label>
              </div>
              <Select 
                value={(formData.periodicity_start_month || 1).toString()} 
                onValueChange={(v) => setFormData({ ...formData, periodicity_start_month: parseInt(v) })}
              >
                <SelectTrigger data-testid="start-month-select">
                  <SelectValue placeholder="Seleccionar mes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Enero (predeterminado)</SelectItem>
                  <SelectItem value="2">Febrero</SelectItem>
                  <SelectItem value="3">Marzo</SelectItem>
                  <SelectItem value="4">Abril</SelectItem>
                  <SelectItem value="5">Mayo</SelectItem>
                  <SelectItem value="6">Junio</SelectItem>
                  <SelectItem value="7">Julio</SelectItem>
                  <SelectItem value="8">Agosto</SelectItem>
                  <SelectItem value="9">Septiembre</SelectItem>
                  <SelectItem value="10">Octubre</SelectItem>
                  <SelectItem value="11">Noviembre</SelectItem>
                  <SelectItem value="12">Diciembre</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {formData.periodicity === 'quarterly' 
                  ? `Los trimestres iniciarán desde el mes seleccionado. Ejemplo: Marzo → T1 (Mar-May), T2 (Jun-Ago), etc.`
                  : `Los semestres iniciarán desde el mes seleccionado. Ejemplo: Marzo → S1 (Mar-Ago), S2 (Sep-Feb)`}
              </p>
            </div>
          )}

          {/* Hierarchy Configuration Section */}
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <GitBranch className="w-4 h-4 text-slate-600" />
                <Label className="text-sm font-medium">Desglose jerárquico</Label>
              </div>
              <Switch
                checked={formData.has_hierarchy}
                onCheckedChange={(checked) => setFormData({ ...formData, has_hierarchy: checked })}
                data-testid="hierarchy-toggle"
              />
            </div>
            
            {formData.has_hierarchy && (
              <div className="space-y-3 pl-6 border-l-2 border-slate-200">
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Nombre de la dimensión</Label>
                  <Input
                    value={formData.hierarchy_dimension}
                    onChange={(e) => setFormData({ ...formData, hierarchy_dimension: e.target.value })}
                    placeholder="Ej: Estrategia, Canal, Región"
                    className="h-8 text-sm"
                    data-testid="hierarchy-dimension-input"
                  />
                </div>
                
                {/* Add Parent Node */}
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500">Agregar nodo padre</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newNodeName}
                      onChange={(e) => setNewNodeName(e.target.value)}
                      placeholder="Ej: ANDREA, COPPEL..."
                      className="h-8 text-sm flex-1"
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addParentNode())}
                    />
                    <Button type="button" size="sm" onClick={addParentNode} className="h-8">
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                
                {/* Hierarchy Tree */}
                {formData.hierarchy_nodes.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500">Estructura ({formData.hierarchy_dimension})</Label>
                    <div className="bg-slate-50 rounded-lg p-2 max-h-48 overflow-y-auto">
                      {formData.hierarchy_nodes
                        .filter(n => !n.parent_id)
                        .map((parentNode) => {
                          const children = formData.hierarchy_nodes.filter(n => n.parent_id === parentNode.id);
                          return (
                            <div key={parentNode.id} className="mb-2">
                              <div className="flex items-center justify-between bg-white rounded px-2 py-1.5 border">
                                <div className="flex items-center gap-2">
                                  <Layers className="w-3 h-3 text-slate-400" />
                                  <span className="text-sm font-medium">{parentNode.name}</span>
                                  {parentNode.is_leaf && (
                                    <Badge variant="outline" className="text-xs px-1 py-0 h-4">Hoja</Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    onClick={() => setSelectedParentForChild(selectedParentForChild === parentNode.id ? null : parentNode.id)}
                                  >
                                    <Plus className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 text-rose-500 hover:text-rose-700"
                                    onClick={() => removeNode(parentNode.id)}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                              
                              {/* Add Child Form */}
                              {selectedParentForChild === parentNode.id && (
                                <div className="flex gap-2 ml-4 mt-1">
                                  <Input
                                    value={newChildName}
                                    onChange={(e) => setNewChildName(e.target.value)}
                                    placeholder="Nombre del hijo"
                                    className="h-7 text-xs flex-1"
                                    autoFocus
                                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addChildNode(parentNode.id))}
                                  />
                                  <Button type="button" size="sm" onClick={() => addChildNode(parentNode.id)} className="h-7 text-xs">
                                    Agregar
                                  </Button>
                                </div>
                              )}
                              
                              {/* Children */}
                              {children.length > 0 && (
                                <div className="ml-4 mt-1 space-y-1">
                                  {children.map((child) => (
                                    <div key={child.id} className="flex items-center justify-between bg-slate-100 rounded px-2 py-1 text-sm">
                                      <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                                        <span>{child.name}</span>
                                        <Badge variant="outline" className="text-xs px-1 py-0 h-4 bg-emerald-50 text-emerald-700">Capturable</Badge>
                                      </div>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-5 w-5 p-0 text-rose-500 hover:text-rose-700"
                                        onClick={() => removeNode(child.id)}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                    <p className="text-xs text-slate-400">
                      Solo los nodos marcados como Hoja o Capturable permiten captura directa. Los padres con hijos se calculan automáticamente.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleClose(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="kpi-form-submit-btn">{saving ? 'Guardando...' : 'Guardar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* Periodicity Change Warning Dialog */}
      <AlertDialog open={showPeriodicityWarning} onOpenChange={setShowPeriodicityWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Cambiar periodicidad
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left space-y-2">
              <p>
                Este KPI ya tiene historial de metas y/o resultados.
              </p>
              <p>
                Cambiar la periodicidad puede afectar la interpretación histórica de los datos.
              </p>
              <p className="text-sm text-slate-500">
                Los datos existentes se mantendrán vinculados a sus periodos originales. 
                Los nuevos registros utilizarán la nueva periodicidad ({pendingPeriodicity && getPeriodicityLabel(pendingPeriodicity)}).
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelPeriodicityChange}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPeriodicityChange} className="bg-amber-600 hover:bg-amber-700">
              Confirmar cambio
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default KPIForm;
